import L from "leaflet";
import type { ForteXApi, LayerChange, LayerEvents, MapObject, MasterDataModel } from "@fortex/api";
import { ForteXApiError } from "@fortex/api";
import { loadCatalogue } from "../catalogue/loadCatalogue";
import { applyCounts, applyLayerChange, applyLayerSummaries, catalogueIconUrl, catalogueTileUrl, hasPendingLayers, normaliseCatalogue } from "../catalogue/normalise";
import type { Catalogue } from "../catalogue/types";
import { createDrawSession } from "../draw/DrawSession";
import type { DrawSession } from "../draw/DrawSession";
import { FeatureIndex } from "../features/FeatureIndex";
import { FeatureRenderer } from "../features/FeatureRenderer";
import { entryColor } from "../features/style";
import type { Feature } from "../features/types";
import { resolveLabels } from "../i18n/labels";
import type { Labels } from "../i18n/labels";
import { createFeatureInfo } from "../info/FeatureInfo";
import type { FeatureInfoController } from "../info/FeatureInfo";
import { createSelection } from "../info/Selection";
import type { SelectionController } from "../info/Selection";
import { LayerManager } from "../layers/LayerManager";
import { createDefaultRegistry } from "../sources/registry";
import type { SourceRegistry, TrackPlayback } from "../sources/types";
import { zIndexBands } from "../sources/types";
import type { LayersApi, MapCoreApi, MapCoreEvents, Unsubscribe } from "./api";
import { createEmitter } from "./events";
import { createMeasureController } from "./measure";
import type { MeasureController } from "./measure";
import { resolveOptions } from "./options";
import type { ForteXMapConfig, LatLngTuple, ResolvedOptions, Theme } from "./options";
import { readPersistedState, writePersistedState } from "./persisted";
import type { PersistedState } from "./persisted";
import { createSearchController } from "./search";
import type { SearchController } from "./search";
import { createMapStore } from "./store";
import type { MapStore, PanelName, SideTab } from "./store";
import { errorMessage, errorStatus, isEditableTarget, isTouchDevice, prefersReducedMotion } from "./support";
import { applyTheme, watchSystemTheme } from "./theme";
import { createUrlBinding } from "./urlBinding";
import type { UrlBinding } from "./urlBinding";
import { decode } from "./urlState";
import type { UrlState } from "./urlState";
import { createViewController } from "./view";
import type { ViewController } from "./view";

export type MapCore = MapCoreApi & {
  readonly index: FeatureIndex;
  readonly renderer: FeatureRenderer;
  readonly layerManager: LayerManager;
  readonly sources: SourceRegistry;
  readonly drawSession: DrawSession;
  readonly events: ReturnType<typeof createEmitter<MapCoreEvents>>;
  playback(id: string): TrackPlayback | null;
  layerColor(typeIdName: string): string | null;
  destroyed(): boolean;
};

export type MapCoreOptions = {
  sources?: SourceRegistry;
  animate?: boolean;
  touch?: boolean;
  window?: Window;
  layerPollMs?: number;
};

export const catalogueRetryLimit = 2;

export const catalogueRetryDelayMs = 400;

export const layerPollMs = 5000;

export async function readLayerTrees(api: ForteXApi, signal?: AbortSignal): Promise<MasterDataModel[]> {
  try {
    const [maps, layers] = await Promise.all([api.tree("MAP", signal), api.tree("LAYER", signal)]);

    return [...maps, ...layers];
  } catch (error) {
    if (!(error instanceof ForteXApiError) || !(error.status === 401 || error.status === 403) || !api.authenticated) throw error;

    const [maps, layers] = await Promise.all([api.publicTree("MAP", signal), api.publicTree("LAYER", signal)]);

    return [...maps, ...layers];
  }
}

export function initialSideTab(options: ResolvedOptions, initialUrl: UrlState | null): SideTab {
  const searching = options.controls.search.enabled && (initialUrl?.panel === "search" || (initialUrl?.query ?? "").trim().length > 0);

  if (searching) return "results";
  if (options.controls.layersTab) return "layers";

  return options.controls.search.enabled ? "results" : "selected";
}

export async function loadCatalogueWithFallback(api: ForteXApi, langId: number, signal?: AbortSignal, onComplete?: (catalogue: Catalogue) => void): Promise<Catalogue> {
  try {
    return await loadCatalogue(api, { langId, signal, onComplete });
  } catch (error) {
    if (!(error instanceof ForteXApiError) || !(error.status === 401 || error.status === 403) || !api.authenticated) throw error;

    const [maps, layers] = await Promise.all([api.publicTree("MAP", signal), api.publicTree("LAYER", signal)]);

    return normaliseCatalogue({ langId, maps, layers, objectLayers: null, iconUrl: catalogueIconUrl(api), tileUrlTemplate: catalogueTileUrl(api) });
  }
}

export function createMapCore(container: HTMLElement, api: ForteXApi, config?: ForteXMapConfig, coreOptions: MapCoreOptions = {}): MapCore {
  const options: ResolvedOptions = resolveOptions(config);
  const windowRef = coreOptions.window ?? (typeof window === "undefined" ? null : window);
  const initialUrl: UrlState | null = options.urlState && windowRef ? decode(windowRef.location.search) : null;
  const lang = initialUrl?.lang ?? options.lang;
  let labels: Labels = resolveLabels(lang, options.labels);
  const persisted: PersistedState = readPersistedState(options.storageKey);
  const animate = coreOptions.animate ?? !prefersReducedMotion();
  const touch = coreOptions.touch ?? isTouchDevice();
  const events = createEmitter<MapCoreEvents>();
  const disposers: (() => void)[] = [];
  let destroyed = false;

  if (lang !== options.lang) {
    options.lang = lang;
    options.langId = lang === "en" ? 2 : 1;
  }

  if (initialUrl?.theme) options.theme = initialUrl.theme;
  if (initialUrl?.chrome) options.chrome = initialUrl.chrome;

  api.setLangId(options.langId);

  const store: MapStore = createMapStore({
    theme: options.theme,
    langId: options.langId,
    view: { center: initialUrl?.center ?? options.view.center, zoom: initialUrl?.zoom ?? options.view.zoom, bounds: null },
    coordinateSystem: persisted.coordinateSystem,
    panelPinned: persisted.panelPinned,
    panel: initialUrl?.panel ?? null,
    sideTab: initialSideTab(options, initialUrl),
    search: { term: initialUrl?.query ?? "", tab: initialUrl?.tab === "address" ? "address" : "objects", loading: false, objects: [], addresses: [], recent: [], error: null, filterFields: [], filters: {} },
    editing: api.authenticated
  });

  const savePersisted = (patch: Partial<PersistedState>) => {
    Object.assign(persisted, patch);
    writePersistedState(options.storageKey, persisted);
  };

  const root: HTMLElement = container.closest<HTMLElement>(".fxmap") ?? container.parentElement ?? container;

  const map = L.map(container, {
    zoomControl: false,
    attributionControl: false,
    boxZoom: false,
    minZoom: options.view.minZoom,
    maxZoom: options.view.maxZoom,
    zoomAnimation: animate,
    fadeAnimation: animate,
    markerZoomAnimation: animate,
    keyboard: true,
    worldCopyJump: false
  }).setView(store.getState().view.center, store.getState().view.zoom);

  map.createPane("fxmap-selection").style.zIndex = String(zIndexBands.selection);
  map.createPane("fxmap-draw").style.zIndex = String(zIndexBands.draw);

  const index = new FeatureIndex();
  let selection: SelectionController;
  let info: FeatureInfoController;
  let layerManager: LayerManager;

  const layerColor = (typeIdName: string): string | null => {
    const entry = layerManager?.catalogueEntries().find(a => a.idName === typeIdName || a.source?.typeIdName === typeIdName) ?? null;

    return entry === null ? null : entryColor(entry);
  };

  const renderer = new FeatureRenderer({
    map,
    index,
    animate,
    layerColor,
    onClick: (feature, latlng, originalEvent) => {
      if (drawSession.active() || measure.active()) return;

      selection.onFeatureClick(feature, originalEvent);
      info.onFeatureClick(feature, latlng);
      events.emit("feature:click", { feature, latlng });
    },
    onHover: feature => {
      store.setState({ hovered: feature?.object ?? null });
      events.emit("feature:hover", { feature });
    },
    onClusterClick: (feature, latlng) => {
      info.onClusterClick(feature);
      map.setView(latlng, Math.min(map.getZoom() + 2, map.getMaxZoom()), { animate });
      events.emit("feature:click", { feature, latlng });
    }
  });

  const sources = coreOptions.sources ?? createDefaultRegistry(api, { extra: options.sources });

  layerManager = new LayerManager({
    map,
    api,
    store,
    options,
    labels,
    sources,
    renderer,
    index,
    events,
    persisted,
    savePersisted,
    initialOn: initialUrl?.on ?? null,
    animate
  });

  selection = createSelection({ map, container, store, options: options.selection, index, renderer, events });
  info = createFeatureInfo({ map, api, store, labels, options: options.info, lang: () => (store.getState().langId === 2 ? "en" : "hu"), index, layers: layerManager, events });

  const view: ViewController = createViewController({
    map,
    container,
    store,
    labels,
    animate,
    contentBounds: () => layerManager.contentBounds(),
    fallbackBounds: options.view.fitTo !== null && options.view.fitTo !== "content" ? options.view.fitTo : null,
    homeView: { center: options.view.center, zoom: options.view.zoom }
  });

  const measure: MeasureController = createMeasureController({ map, store, labels });

  const drawSession: DrawSession = createDrawSession({
    map,
    root,
    labels,
    touch,
    reducedMotion: !animate,
    options: initialUrl?.draw && options.draw.editable ? { ...options.draw, mode: initialUrl.draw, wkt: initialUrl.wkt ?? options.draw.wkt } : options.draw,
    events,
    store,
    snapCandidates: () => index.snapCandidates()
  });

  const search: SearchController = createSearchController({
    api,
    store,
    labels,
    options: options.controls.search,
    layers: layerManager,
    view,
    admin: () => options.info.admin ?? api.authenticated,
    onPickObject: object => {
      selection.set([object.id]);
      info.openObject(object);
    },
    persisted,
    savePersisted
  });

  disposers.push(events.on("selection:change", ({ ids, objects }) => info.onSelectionChange(ids, objects)));
  disposers.push(events.on("layers:loaded", () => selection.reapply()));
  disposers.push(events.on("draw:mode", ({ mode }) => mode !== null && measure.active() && measure.cancel()));

  const syncTheme = () => {
    const dark = applyTheme(container, { theme: store.getState().theme, darkBaseMap: layerManager.isDark() });

    if (store.getState().dark !== dark) {
      store.setState({ dark });
      events.emit("theme:change", { dark });
    }
  };

  disposers.push(events.on("layers:change", ({ entry, reason }) => reason === "selected" && entry.root === "maps" && syncTheme()));
  disposers.push(watchSystemTheme(syncTheme));
  syncTheme();

  const emitView = () => {
    const center = map.getCenter();
    const bounds = map.getBounds();

    store.setState({
      view: {
        center: [center.lat, center.lng],
        zoom: map.getZoom(),
        bounds: [
          [bounds.getSouth(), bounds.getWest()],
          [bounds.getNorth(), bounds.getEast()]
        ]
      }
    });
    events.emit("view:change", { center: [center.lat, center.lng], zoom: map.getZoom() });
  };

  const onMapClick = (event: L.LeafletMouseEvent) => {
    if (drawSession.active() || measure.active()) return;

    const latlng: LatLngTuple = [event.latlng.lat, event.latlng.lng];

    selection.onMapClick();
    void info.onMapClick(latlng);
    events.emit("map:click", { latlng });
  };

  const onMouseMove = (event: L.LeafletMouseEvent) => {
    if (touch) return;

    store.setState({ cursor: [event.latlng.lat, event.latlng.lng] });
  };

  const onMouseOut = () => {
    store.setState({ cursor: null });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const inside = event.target instanceof Node && root.contains(event.target);

    if (!inside && event.target !== document.body && event.target !== document) return;
    if (isEditableTarget(event.target) && event.key !== "Escape") return;

    if (event.key === "Escape") {
      const handled = drawSession.active() || measure.handleKey(event) || selection.handleEscape() || info.handleEscape() || closePanel();

      events.emit("escape", { handled });
      return;
    }

    if (measure.handleKey(event)) return;
  };

  const closePanel = (): boolean => {
    if (store.getState().panel === null || store.getState().panelPinned) return false;

    store.getState().setPanel(null);

    return true;
  };

  map.on("moveend", emitView);
  map.on("zoomend", emitView);
  map.whenReady(emitView);
  map.on("click", onMapClick);
  map.on("mousemove", onMouseMove);
  map.on("mouseout", onMouseOut);
  document.addEventListener("keydown", onKeyDown);
  disposers.push(() => {
    map.off("moveend", emitView);
    map.off("zoomend", emitView);
    map.off("click", onMapClick);
    map.off("mousemove", onMouseMove);
    map.off("mouseout", onMouseOut);
    document.removeEventListener("keydown", onKeyDown);
  });

  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => map.invalidateSize({ animate: false }));

  observer?.observe(container);
  disposers.push(() => observer?.disconnect());

  let urlBinding: UrlBinding | null = null;

  const applyUrl = (state: UrlState) => {
    if (state.center && state.zoom !== null) map.setView(state.center, state.zoom, { animate: false });
    else if (state.center) map.setView(state.center, map.getZoom(), { animate: false });

    if (state.on !== null) {
      const on = state.on;
      const entries = layerManager.registry.list().filter(a => a.entry.origin !== "host");
      const namesBaseMap = entries.some(a => a.root === "maps" && on.some(token => token === a.key || token === a.entry.idName));

      for (const entry of entries) {
        if (entry.root === "maps" && !namesBaseMap) continue;

        layerManager.setSelected(entry.id, on.some(token => token === entry.key || token === entry.entry.idName));
      }
    }

    if (state.theme && state.theme !== store.getState().theme) setTheme(state.theme);
    if (state.panel !== store.getState().panel) store.getState().setPanel(state.panel);

    selection.set(state.objectIds);

    if (state.feature) void info.openFeature(state.feature.layer, state.feature.id);
    else if (store.getState().info.kind === "tile") info.close();

    if (state.query !== null && state.query !== store.getState().search.term) search.setTerm(state.query);
  };

  if (options.urlState && windowRef) {
    urlBinding = createUrlBinding({ state: () => store.getState(), selectedKeys: () => layerManager.selectedKeys(), options }, { apply: applyUrl }, windowRef.history, windowRef.location);
    disposers.push(store.subscribe(state => state.ready && urlBinding?.write()));
    disposers.push(() => urlBinding?.dispose());
  }

  let contentFitted = false;

  const setTheme = (theme: Theme) => {
    options.theme = theme;
    store.setState({ theme });
    syncTheme();
  };

  let catalogueAbort: AbortController | null = null;
  let layerEvents: Promise<LayerEvents | null> | null = null;
  let layerPollTimer: ReturnType<typeof setTimeout> | null = null;
  let layerPollAbort: AbortController | null = null;
  const pollDelayMs = coreOptions.layerPollMs ?? layerPollMs;

  const scheduleLayerPoll = () => {
    const current = store.getState().catalogue;

    if (destroyed || layerPollTimer !== null || layerPollAbort !== null || current === null || !hasPendingLayers(current)) return;

    layerPollTimer = setTimeout(() => {
      layerPollTimer = null;
      void pollLayers();
    }, pollDelayMs);
  };

  const pollLayers = async (): Promise<void> => {
    const controller = new AbortController();

    layerPollAbort = controller;

    const rows = await readLayerTrees(api, controller.signal).catch(() => null);

    layerPollAbort = null;

    const current = store.getState().catalogue;

    if (destroyed || controller.signal.aborted || current === null) return;

    const changed = rows === null ? null : applyLayerSummaries(current, rows);

    if (changed !== null) {
      store.setState({ catalogue: changed });
      layerManager.update(changed);
    }

    scheduleLayerPoll();
  };

  disposers.push(() => {
    if (layerPollTimer !== null) clearTimeout(layerPollTimer);

    layerPollTimer = null;
    layerPollAbort?.abort();
  });

  const onLayerChange = (change: LayerChange) => {
    const current = store.getState().catalogue;

    if (destroyed || current === null) return;

    const changed = applyLayerChange(current, change);

    if (changed === null) return;

    store.setState({ catalogue: changed });
    layerManager.update(changed);
    scheduleLayerPoll();
  };

  const openLayerEvents = () => {
    if (layerEvents !== null || !api.authenticated) return;

    const pending = api.connectLayerEvents(onLayerChange).catch(() => null);

    layerEvents = pending;
    disposers.push(() => void pending.then(handle => handle?.stop()).catch(() => undefined));
  };

  const loadCatalogueRetrying = async (langId: number, signal: AbortSignal, attempt = 0): Promise<Catalogue> => {
    try {
      return await loadCatalogueWithFallback(api, langId, signal, completed => {
        const current = store.getState().catalogue;

        if (destroyed || current === null || store.getState().langId !== langId) return;

        const counted = applyCounts(current, completed);

        if (counted === null) return;

        store.setState({ catalogue: counted });
        layerManager.update(counted);
      });
    } catch (error) {
      if (attempt >= catalogueRetryLimit || signal.aborted || errorStatus(error) !== 0) throw error;

      await new Promise<void>(resolve => setTimeout(resolve, catalogueRetryDelayMs));

      if (signal.aborted) throw error;

      return loadCatalogueRetrying(langId, signal, attempt + 1);
    }
  };

  const reloadCatalogue = async (): Promise<void> => {
    const langId = store.getState().langId;

    catalogueAbort?.abort();
    catalogueAbort = new AbortController();

    const signal = catalogueAbort.signal;

    try {
      const catalogue = await loadCatalogueRetrying(langId, signal);

      if (destroyed || signal.aborted) return;

      const first = store.getState().catalogue === null;

      store.setState({ catalogue, catalogueError: null });
      layerManager.build(catalogue);
      scheduleLayerPoll();

      if (first) {
        for (const definition of options.layers.extra) layerManager.addHost(definition);

        store.setState({ ready: true });
        events.emit("ready", { catalogue });
        openLayerEvents();

        if (initialUrl) {
          selection.set(initialUrl.objectIds);

          if (initialUrl.feature) void info.openFeature(initialUrl.feature.layer, initialUrl.feature.id);
          if (initialUrl.query) search.setTerm(initialUrl.query);
        }
      }

      syncTheme();
    } catch (error) {
      if (destroyed || signal.aborted) return;

      const message = errorMessage(error, labels);

      store.setState({ catalogueError: message, ready: true });
      events.emit("catalogue:error", { message });
      events.emit("error", { message, status: errorStatus(error) });
    }
  };

  disposers.push(
    events.on("layers:loaded", () => {
      if (contentFitted || options.view.fitTo !== "content" || initialUrl?.center) return;

      const bounds = layerManager.contentBounds();

      if (bounds === null) return;

      contentFitted = true;
      view.fitContent();
    })
  );

  if (options.view.fitTo !== null && options.view.fitTo !== "content" && !initialUrl?.center) {
    map.fitBounds(L.latLngBounds(options.view.fitTo[0], options.view.fitTo[1]), { padding: [48, 48], animate: false });
  }

  emitView();
  void reloadCatalogue();

  const layers: LayersApi = {
    list: () => layerManager.list(),
    get: id => layerManager.get(id),
    entry: id => layerManager.entryOf(id),
    setSelected: (id, on) => layerManager.setSelected(id, on),
    toggleGroup: (groupKey, on) => layerManager.toggleGroup(groupKey, on),
    groupState: groupKey => layerManager.groupState(groupKey),
    setOpacity: (id, value) => layerManager.setOpacity(id, value),
    solo: id => layerManager.solo(id),
    fit: id => layerManager.fit(id),
    allOn: (root, on) => layerManager.allOn(root, on),
    reset: () => layerManager.reset(),
    refresh: () => layerManager.refresh(),
    addHost: definition => layerManager.addHost(definition),
    removeHost: key => layerManager.removeHost(key),
    features: (): Feature[] => layerManager.features(),
    objects: (): MapObject[] => layerManager.objects(),
    setCollapsed: (groupKey, collapsed) => layerManager.setCollapsed(groupKey, collapsed)
  };

  const core: MapCore = {
    store,
    api,
    options,
    get labels() {
      return labels;
    },
    map,
    container,
    index,
    renderer,
    layerManager,
    sources,
    drawSession,
    events,
    layers,
    selection,
    draw: drawSession,
    view,
    info,
    search,
    measure,
    catalogue: () => store.getState().catalogue,
    reloadCatalogue,
    setTheme,
    setLangId: langId => {
      if (store.getState().langId === langId) return;

      const nextLang = langId === 2 ? "en" : "hu";

      options.lang = nextLang;
      options.langId = langId;
      labels = resolveLabels(nextLang, options.labels);
      api.setLangId(langId);
      store.setState({ langId });
      events.emit("lang:change", { langId });
      void reloadCatalogue();
    },
    setPanel: (panel: PanelName) => store.getState().setPanel(panel),
    setCoordinateSystem: system => {
      store.setState({ coordinateSystem: system });
      savePersisted({ coordinateSystem: system });
    },
    markHintSeen: id => {
      if (!persisted.hintsSeen.includes(id)) savePersisted({ hintsSeen: [...persisted.hintsSeen, id] });
      if (store.getState().hint?.id === id) store.setState({ hint: null });
    },
    on: <K extends keyof MapCoreEvents>(event: K, handler: (payload: MapCoreEvents[K]) => void): Unsubscribe => events.on(event, handler),
    playback: id => layerManager.playback(id),
    layerColor,
    destroyed: () => destroyed,
    destroy: () => {
      if (destroyed) return;

      destroyed = true;
      catalogueAbort?.abort();

      for (const dispose of [...disposers].reverse()) dispose();

      search.dispose();
      drawSession.dispose();
      measure.dispose();
      info.dispose();
      selection.dispose();
      view.dispose();
      layerManager.dispose();
      events.clear();
      map.remove();
      container.classList.remove("fxmap-dark");
    }
  };

  return core;
}
